/**
 * src/components/auth/UserMenu.tsx
 * =============================================================================
 * Botón circular con la inicial del usuario + dropdown.
 *
 * Contenido del dropdown (top→bottom):
 *   1. Header con nombre + email.
 *   2. Selector de espacio activo (solo si hay >1) + "Crear espacio nuevo".
 *   3. "Miembros del espacio" (solo si el activo NO es personal Y user es admin).
 *   4. "Cuenta" — abre AccountModal vía `onOpenAccount`.
 *   5. "Cerrar sesión".
 *
 * Cierra al click fuera y al pulsar Escape.
 * =============================================================================
 */

import { useCallback, useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Check, Loader2, LogOut, Plus, Users } from 'lucide-react'
import { useAuth } from '@/components/providers/AuthProvider'
import { useActiveSpace } from '@/components/providers/ActiveSpaceProvider'
import { CreateSpaceModal } from '@/components/auth/CreateSpaceModal'
import { SpaceMembersModal } from '@/components/auth/SpaceMembersModal'
import { leaveSpace, SpacesError } from '@/services/spacesService'

interface Props {
  /**
   * Callback que abre el modal de "Cuenta y facturación". Lo monta el
   * padre porque vive a nivel de página (necesita su propio z-index alto
   * y compartir el viewport). Si no se pasa, el item "Facturación" no se
   * renderiza.
   */
  onOpenAccount?: () => void
}

export function UserMenu({ onOpenAccount }: Props) {
  const { t } = useTranslation()
  const { user, signOut } = useAuth()
  const navigate = useNavigate()
  const { spaces, active, setActiveSpaceId, refresh } = useActiveSpace()
  const [open, setOpen] = useState(false)
  const [createSpaceOpen, setCreateSpaceOpen] = useState(false)
  const [membersOpen, setMembersOpen] = useState(false)
  const [leaving, setLeaving] = useState(false)
  /**
   * Confirmación de "abandonar espacio" EN LÍNEA dentro del dropdown, en vez
   * del window.confirm/alert nativo de antes: mismo patrón que la fila
   * "¿Borrar X? Sí/No" de MisDocumentosDrawer y el confirm de AccountModal.
   * Los diálogos nativos no se pueden traducir con el resto de la UI ni
   * respetan el estilo de la app.
   */
  const [confirmingLeave, setConfirmingLeave] = useState(false)
  /** Código del último fallo al salir del espacio, o null. Se pinta como
   *  línea roja bajo la confirmación en vez del window.alert de antes. */
  const [leaveError, setLeaveError] = useState<string | null>(null)
  const wrapperRef = useRef<HTMLDivElement>(null)

  async function onLeaveActive() {
    if (!active || active.isPersonal || leaving) return
    setLeaving(true)
    setLeaveError(null)
    try {
      await leaveSpace(active.id)
      await refresh()
      setConfirmingLeave(false)
      setOpen(false)
    } catch (err) {
      const code = err instanceof SpacesError ? err.code : 'request_failed'
      setLeaveError(code)
    } finally {
      setLeaving(false)
    }
  }

  const close = useCallback(() => {
    setOpen(false)
    // Al cerrar el menú, la confirmación pendiente se descarta: reabrir el
    // menú no debe mostrar un "¿Salir?" de una intención anterior.
    setConfirmingLeave(false)
    setLeaveError(null)
  }, [])

  useEffect(() => {
    if (!open) return
    const onClick = (e: MouseEvent) => {
      if (!wrapperRef.current?.contains(e.target as Node)) close()
    }
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close()
    }
    document.addEventListener('mousedown', onClick)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onClick)
      document.removeEventListener('keydown', onKey)
    }
  }, [open, close])

  if (!user) return null

  const initial = (user.name?.trim()?.[0] ?? user.email?.[0] ?? '?').toUpperCase()
  const showSpaceList = spaces.length > 1
  const canManageMembers =
    active !== null && !active.isPersonal && active.role === 'admin'

  return (
    <>
      <div ref={wrapperRef} className="relative">
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          aria-haspopup="menu"
          aria-expanded={open}
          title={user.name ?? user.email}
          // El hover anterior era `bg-zinc-800` sobre `bg-zinc-900`: dos tonos
          // casi idénticos, imperceptible en pantallas sin calibrar. Ahora
          // `zinc-700` + un halo claro alrededor, que además funciona sobre
          // el fondo zinc-100 de la pizarra. El avatar es el único acceso al
          // menú de cuenta: tiene que parecer pulsable.
          className="h-8 w-8 rounded-full bg-zinc-900 text-white text-xs font-medium flex items-center justify-center hover:bg-zinc-700 hover:ring-2 hover:ring-zinc-300 transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-zinc-400"
        >
          {initial}
        </button>

        {open && (
          <div
            role="menu"
            className="absolute right-0 top-10 min-w-[240px] rounded-lg border border-zinc-200 bg-white shadow-lg py-1 z-30"
          >
            <div className="px-3 py-2 border-b border-zinc-100">
              {user.name && (
                <p className="text-sm font-medium text-zinc-900 truncate">{user.name}</p>
              )}
              <p className="text-xs text-zinc-500 truncate">{user.email}</p>
            </div>

            {/* Selector de espacio activo. Solo aparece si hay >1 espacio —
                un user sin colaboraciones no necesita ver "Mi Espacio" como
                opción única. */}
            {showSpaceList && (
              <div className="border-b border-zinc-100 py-1">
                <p className="px-3 py-1 text-[10px] uppercase tracking-wide text-zinc-400 font-medium">
                  {t('panel.espacios')}
                </p>
                {spaces.map((sp) => {
                  const isActive = sp.id === active?.id
                  return (
                    <button
                      key={sp.id}
                      type="button"
                      role="menuitem"
                      onClick={() => {
                        setActiveSpaceId(sp.id)
                        close()
                      }}
                      className="w-full text-left flex items-center justify-between gap-2 px-3 py-1.5 text-sm text-zinc-700 hover:bg-zinc-100"
                    >
                      <div className="min-w-0 flex-1">
                        <p className="truncate">
                          {sp.isPersonal ? t('panel.miEspacio') : sp.name}
                        </p>
                        {!sp.isPersonal && (
                          <p className="text-[11px] text-zinc-500">
                            {t('panel.miembrosCount', { count: sp.memberCount })}
                            {sp.role === 'admin' ? ` · ${t('panel.rolAdmin')}` : ''}
                          </p>
                        )}
                      </div>
                      {isActive && <Check className="h-3.5 w-3.5 shrink-0 text-zinc-900" strokeWidth={2} />}
                    </button>
                  )
                })}
              </div>
            )}

            {/* Crear espacio nuevo — siempre visible. El gating Pro se hace
                en el modal (free user verá CTA a /precios al intentar crear). */}
            <button
              type="button"
              role="menuitem"
              onClick={() => {
                close()
                setCreateSpaceOpen(true)
              }}
              className="w-full text-left flex items-center gap-2 px-3 py-2 text-sm text-zinc-700 hover:bg-zinc-100"
            >
              <Plus className="h-4 w-4" strokeWidth={1.8} />
              {t('panel.crearEspacio')}
            </button>

            {/* Miembros del espacio activo — solo si NO es personal y user
                es admin (no tiene sentido ver miembros del personal porque
                solo existe el propio user). */}
            {canManageMembers && (
              <button
                type="button"
                role="menuitem"
                onClick={() => {
                  close()
                  setMembersOpen(true)
                }}
                className="w-full text-left flex items-center gap-2 px-3 py-2 text-sm text-zinc-700 hover:bg-zinc-100 border-t border-zinc-100"
              >
                <Users className="h-4 w-4" strokeWidth={1.8} />
                {t('panel.miembros')}
              </button>
            )}

            {/* Abandonar este espacio — visible para CUALQUIER miembro de un
                espacio NO personal. El backend bloquea si eres el único admin
                con miembros; en ese caso mostramos el aviso en línea.
                La confirmación es en línea (Sí/No), no window.confirm. */}
            {active && !active.isPersonal && !confirmingLeave && (
              <button
                type="button"
                role="menuitem"
                onClick={() => {
                  setLeaveError(null)
                  setConfirmingLeave(true)
                }}
                disabled={leaving}
                className="w-full text-left flex items-center gap-2 px-3 py-2 text-sm text-red-600 hover:bg-red-50 disabled:opacity-50 border-t border-zinc-100"
              >
                <LogOut className="h-4 w-4" strokeWidth={1.8} />
                {t('menuUsuario.abandonarEspacio')}
              </button>
            )}
            {active && !active.isPersonal && confirmingLeave && (
              <div className="px-3 py-2 border-t border-zinc-100 bg-red-50 space-y-1.5">
                <p className="text-xs text-red-900">
                  {t('menuUsuario.confirmarSalida', { nombre: active.name })}
                </p>
                <div className="flex items-center gap-1.5">
                  <button
                    type="button"
                    onClick={() => {
                      setConfirmingLeave(false)
                      setLeaveError(null)
                    }}
                    disabled={leaving}
                    className="text-xs px-2 h-7 rounded text-zinc-700 hover:bg-white"
                  >
                    {t('menuUsuario.no')}
                  </button>
                  <button
                    type="button"
                    onClick={() => void onLeaveActive()}
                    disabled={leaving}
                    className="text-xs px-2 h-7 rounded bg-red-600 text-white hover:bg-red-700 disabled:opacity-50 inline-flex items-center gap-1.5"
                  >
                    {leaving && <Loader2 className="h-3 w-3 animate-spin" strokeWidth={2} />}
                    {t('menuUsuario.si')}
                  </button>
                </div>
                {leaveError && (
                  <p role="alert" className="text-[11px] text-red-700">
                    {leaveError === 'last_admin_cannot_leave'
                      ? t('menuUsuario.unicoAdmin')
                      : t('menuUsuario.errorSalida')}
                  </p>
                )}
              </div>
            )}

            {onOpenAccount && (
              <button
                type="button"
                role="menuitem"
                onClick={() => {
                  close()
                  onOpenAccount()
                }}
                className="w-full text-left px-3 py-2 text-sm text-zinc-700 hover:bg-zinc-100 border-t border-zinc-100"
              >
                {t('menuUsuario.cuenta')}
              </button>
            )}
            <button
              type="button"
              role="menuitem"
              onClick={async () => {
                close()
                await signOut()
                navigate('/', { replace: true })
              }}
              className="w-full text-left px-3 py-2 text-sm text-zinc-700 hover:bg-zinc-100 border-t border-zinc-100"
            >
              {t('menuUsuario.cerrarSesion')}
            </button>
          </div>
        )}
      </div>

      {createSpaceOpen && (
        <CreateSpaceModal onClose={() => setCreateSpaceOpen(false)} />
      )}
      {membersOpen && active && !active.isPersonal && (
        <SpaceMembersModal space={active} onClose={() => setMembersOpen(false)} />
      )}
    </>
  )
}
